/**
 * CABPage — Main page for the Common Application Blueprint mode.
 *
 * Split layout:
 * - Left panel: File upload area + file list + action bar
 * - Right panel: Preview (empty state initially, later HTML preview)
 * - Progress sidebar: Vertical step progress (visible during generation)
 *
 * Integrates WebSocket for real-time progress updates.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../components/Button/Button';
import { CABFileUpload } from '../components/CAB/CABFileUpload';
import { CABFileList } from '../components/CAB/CABFileList';
import { CABEmptyState } from '../components/CAB/CABEmptyState';
import { CABProgressSidebar } from '../components/CAB/CABProgressSidebar';
import { CABActionBar } from '../components/CAB/CABActionBar';
import { CABSectionSelector, DEFAULT_ENABLED_SECTIONS } from '../components/CAB/CABSectionSelector';
import { CABModelSelector, DEFAULT_MODEL_TIER } from '../components/CAB/CABModelSelector';
import type { CABModelTier } from '../components/CAB/CABModelSelector';
import { useCABWebSocket } from '../hooks/useCABWebSocket';
import { CABErrorState } from '../components/CAB/CABErrorState';
import { CABCostPanel } from '../components/CAB/CABCostPanel';
import { CABShareModal } from '../components/CAB/CABShareModal';
import { IfHasPermission } from '../components/IfHasPermission';
import { Permissions } from '../constants';
import {
  uploadCABFile,
  listCABFiles,
  deleteCABFile,
  downloadCABFile,
  getCABStatus,
  getCABProgress,
  getCABHtml,
  generateCAB,
  deleteCAB,
  pauseCAB,
  cancelCAB,
  resumeCAB,
  regenerateCAB,
  downloadCABMarkdown,
  downloadCABPdf,
  updateCABShareSettings,
  getCABJson,
} from '../services/cabApi';
import type {
  CABFile,
  CABSessionStatus,
  CABPhasesInfo,
  CABErrorInfo,
  CABCostInfo,
  FileUploadState,
} from '../types/cab';
import { PlatformType } from '../types/project';

interface CABPageProps {
  onProjectCostUpdate?: () => void;
  permissions?: string[];
  platformType?: PlatformType;
  onGenerateDesign?: () => void;
}

const CABPage: React.FC<CABPageProps> = ({ onProjectCostUpdate, permissions = [], platformType, onGenerateDesign }) => {
  const { projectId } = useParams<{ projectId: string }>();

  // ────────────────────────────────────────────────────────────
  // STATE
  // ────────────────────────────────────────────────────────────

  const [files, setFiles] = useState<CABFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<FileUploadState[]>([]);
  const [sessionStatus, setSessionStatus] = useState<CABSessionStatus>('idle');
  const [phases, setPhases] = useState<CABPhasesInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<CABErrorInfo | null>(null);
  const [currentProgressStage, setCurrentProgressStage] = useState<string | null>(null);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [costInfo, setCostInfo] = useState<CABCostInfo | null>(null);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingMarkdown, setIsDownloadingMarkdown] = useState(false);
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [enabledSections, setEnabledSections] = useState<string[]>(DEFAULT_ENABLED_SECTIONS);
  const [modelTier, setModelTier] = useState<CABModelTier>(DEFAULT_MODEL_TIER);

  // Guard against stale preview responses: each loadPreview call gets a
  // sequence number. Only the response from the latest call is applied.
  const previewSeqRef = useRef(0);

  // ────────────────────────────────────────────────────────────
  // DATA LOADING
  // ────────────────────────────────────────────────────────────

  const loadData = useCallback(async (showLoading = true) => {
    if (!projectId) return;
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      const [filesRes, statusRes] = await Promise.all([
        listCABFiles(projectId),
        getCABStatus(projectId),
      ]);
      setFiles(filesRes.files);
      setSessionStatus(statusRes.status);
      if (statusRes.phases) {
        setPhases(statusRes.phases);
      }
      if (statusRes.error_info) {
        setErrorInfo(statusRes.error_info);
      }
      if (statusRes.cost) {
        setCostInfo(statusRes.cost);
      }
      if (statusRes.total_duration_seconds != null) {
        setTotalDurationSeconds(statusRes.total_duration_seconds);
      }
      // Load enabled_sections and model_tier from session
      if (statusRes.enabled_sections) {
        setEnabledSections(statusRes.enabled_sections);
      }
      if (statusRes.model_tier) {
        setModelTier(statusRes.model_tier);
      }
      setShareableLinkEnabled(statusRes.readonly_sharable_link_enabled || false);
    } catch (err: any) {
      console.error('Failed to load CAB data:', err);
      setError(extractErrorMessage(err, 'Failed to load data'));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  const loadPreview = useCallback(async (wip = true) => {
    if (!projectId) return;
    const seq = ++previewSeqRef.current;
    try {
      setIsLoadingPreview(true);
      const html = await getCABHtml(projectId, wip);
      // Only apply the result if no newer request has been issued since
      if (seq === previewSeqRef.current) {
        setPreviewHtml(html);
      }
    } catch (err: any) {
      console.warn('Failed to load CAB preview:', err);
      // Don't show error to user — preview is optional during generation
    } finally {
      if (seq === previewSeqRef.current) {
        setIsLoadingPreview(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-load preview when generation is completed (final, non-WIP)
  useEffect(() => {
    if (sessionStatus === 'completed' && projectId && !previewHtml) {
      loadPreview(false);
    }
  }, [sessionStatus, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────────────────
  // WEBSOCKET
  // ────────────────────────────────────────────────────────────

  useCABWebSocket({
    projectId,
    enabled: sessionStatus === 'processing',
    onProgressUpdate: (data) => {
      if (data.phases) setPhases(data.phases);
      if (data.current_phase_name) setCurrentProgressStage(data.current_phase_name);
      if (data.overall_progress?.percentage !== undefined) {
        setOverallPercentage(data.overall_progress.percentage);
      }
      if (data.elapsed_seconds != null) {
        setTotalDurationSeconds(data.elapsed_seconds);
      }
    },
    onPhaseCompleted: (phase) => {
      // Refresh files to get updated parse statuses
      if (phase === 'file_processing' && projectId) {
        listCABFiles(projectId).then((res) => setFiles(res.files)).catch(() => {});
      }
    },
    onSkeletonReady: () => {
      // Load WIP preview as soon as skeleton is ready
      if (projectId) {
        loadPreview();
      }
    },
    onSectionReady: (section) => {
      // Reload preview as each section completes
      if (projectId) {
        loadPreview();
      }
    },
    onCompleted: (data) => {
      setSessionStatus('completed');
      setCurrentProgressStage(null);
      if (data?.total_duration_seconds != null) {
        setTotalDurationSeconds(data.total_duration_seconds);
      }
      loadData(false);
      loadPreview(false);
      onProjectCostUpdate?.();
    },
    onError: (data) => {
      setSessionStatus('error');
      setError(data?.message || 'Generation failed');
    },
    onPaused: (data) => {
      setSessionStatus('paused');
      setCurrentProgressStage(null);
      loadData(false);
    },
    onCancelled: (data) => {
      setSessionStatus('cancelled');
      setCurrentProgressStage(null);
      setPhases(null);
      loadData(false);
    },
  });

  // ────────────────────────────────────────────────────────────
  // POLLING FALLBACK — catches up when WebSocket is slow/missed
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (sessionStatus !== 'processing' || !projectId) return;

    let isMounted = true;
    let lastSkeletonStatus: string | undefined;
    let lastSectionGenerationStatus: string | undefined;

    const pollProgress = async () => {
      try {
        const progress = await getCABProgress(projectId);
        if (!isMounted) return;

        if (progress.phases) {
          // Only update phases if data actually changed (avoids flicker)
          setPhases((prev) => {
            const newJson = JSON.stringify(progress.phases);
            const prevJson = prev ? JSON.stringify(prev) : '';
            return newJson !== prevJson ? progress.phases : prev;
          });

          // Check if skeleton just became ready
          const skeletonStatus = progress.phases.skeleton_assemble?.status;
          if (skeletonStatus === 'completed' && lastSkeletonStatus !== 'completed') {
            loadPreview(); // Load WIP preview with skeleton
          }
          lastSkeletonStatus = skeletonStatus;

          // Check if section generation is running — refresh preview periodically
          const sectionGenStatus = progress.phases.section_generation?.status;
          if (sectionGenStatus === 'running' && previewHtml) {
            loadPreview(); // Refresh to pick up newly completed sections
          }
          lastSectionGenerationStatus = sectionGenStatus;
        }
        if (progress.current_phase_name) {
          setCurrentProgressStage((prev) => prev !== progress.current_phase_name ? progress.current_phase_name : prev);
        }
        if (progress.overall_progress?.percentage !== undefined) {
          setOverallPercentage((prev) => prev !== progress.overall_progress.percentage ? progress.overall_progress.percentage : prev);
        }

        // Detect terminal states from the backend
        if (progress.status === 'completed') {
          setSessionStatus('completed');
          setCurrentProgressStage(null);
          loadData(false);
          loadPreview(false); // Final preview (non-WIP)
          onProjectCostUpdate?.();
          return; // stop polling
        }
        if (progress.status === 'error') {
          setSessionStatus('error');
          loadData(false);
          return; // stop polling
        }
        if (progress.status === 'paused' || progress.status === 'cancelled') {
          setSessionStatus(progress.status);
          return; // stop polling
        }
      } catch (err) {
        console.warn('CAB progress poll failed:', err);
      }
    };

    // Poll immediately on mount, then every 30 seconds (WebSocket is the primary channel)
    pollProgress();
    const intervalId = setInterval(pollProgress, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [sessionStatus, projectId, previewHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────────────────
  // FILE HANDLERS
  // ────────────────────────────────────────────────────────────

  /** Safely extract a human-readable error string from an Axios error. */
  const extractErrorMessage = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    // FastAPI 422 returns detail as an array of validation-error objects
    if (Array.isArray(detail)) {
      const msgs = detail.map((d: any) => d?.msg).filter(Boolean);
      return msgs.length > 0 ? msgs.join('; ') : fallback;
    }
    return fallback;
  };

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (!projectId) return;

    setIsUploading(true);
    setError(null);

    // Create initial upload states for all files with AbortControllers
    const initialStates: FileUploadState[] = files.map((file, idx) => ({
      tempId: `temp-${Date.now()}-${idx}`,
      file,
      status: 'pending',
      progress: 0,
      abortController: new AbortController(),
    }));

    // Immediately show files in UI with "pending" status
    setUploadingFiles(initialStates);

    // Upload all files in parallel
    const uploadPromises = initialStates.map(async (uploadState) => {
      try {
        // Update status to "uploading"
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'uploading' as const }
              : s
          )
        );

        const result = await uploadCABFile(
          projectId,
          uploadState.file,
          (percent) => {
            // Update individual file progress
            setUploadingFiles((prev) =>
              prev.map((s) =>
                s.tempId === uploadState.tempId
                  ? { ...s, progress: percent }
                  : s
              )
            );
          },
          uploadState.abortController
        );

        // Mark as completed
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? {
                  ...s,
                  status: 'completed' as const,
                  progress: 100,
                  fileId: result.file_id,
                }
              : s
          )
        );

        return { success: true, result };
      } catch (err: any) {
        // Check if error is due to abort
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('File upload cancelled:', uploadState.file.name);
          return { success: false, cancelled: true };
        }

        console.error('File upload failed:', err);
        const errorMsg = extractErrorMessage(err, 'Upload failed');

        // Mark as error
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'error' as const, error: errorMsg }
              : s
          )
        );

        return { success: false, error: err };
      }
    });

    // Wait for all uploads to complete (or fail)
    const results = await Promise.allSettled(uploadPromises);

    // Calculate overall progress
    const successCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.success
    ).length;
    const cancelledCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.cancelled
    ).length;
    const failCount = results.length - successCount - cancelledCount;

    // Show error if any uploads failed (but not if they were cancelled)
    if (failCount > 0) {
      setError(`${failCount} file(s) failed to upload`);
    }

    // Refresh file list from server, then clear uploading state so
    // there's no moment where both lists show the same file.
    await loadData(false);

    // Server list is now in `files[]` — safe to clear uploading entries.
    // Keep error entries briefly so user can see what failed.
    setUploadingFiles((prev) =>
      prev.filter((s) => s.status === 'error')
    );
    setIsUploading(false);

    if (failCount > 0) {
      setTimeout(() => {
        setUploadingFiles([]);
      }, 3000);
    }
  }, [projectId, loadData, extractErrorMessage]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await deleteCABFile(projectId, fileId);
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete file'));
    }
  }, [projectId]);

  const handleDownloadFile = useCallback(async (fileId: string, fileName: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await downloadCABFile(projectId, fileId, fileName);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to download file'));
    }
  }, [projectId]);

  const handleDeleteAll = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);

      // STEP 1: Cancel all in-flight uploads atomically
      uploadingFiles.forEach((uploadState) => {
        if (uploadState.abortController && uploadState.status === 'uploading') {
          uploadState.abortController.abort();
        }
      });

      // STEP 2: Clear upload state immediately (UX feedback)
      setUploadingFiles([]);
      setIsUploading(false);

      // STEP 3: Small delay to ensure abort signals propagate through network stack
      await new Promise(resolve => setTimeout(resolve, 100));

      // STEP 4: Now safely delete everything from backend
      await deleteCAB(projectId);

      // STEP 5: Clear remaining state
      setFiles([]);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setCostInfo(null);
      setTotalDurationSeconds(null);
      setEnabledSections(DEFAULT_ENABLED_SECTIONS);
      setModelTier(DEFAULT_MODEL_TIER);
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete'));
    }
  }, [projectId, uploadingFiles, onProjectCostUpdate]);

  // ────────────────────────────────────────────────────────────
  // GENERATION HANDLERS
  // ────────────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (!projectId) return;
    if (enabledSections.length === 0) {
      setError('Select at least one section to generate');
      return;
    }
    try {
      setError(null);
      await generateCAB(projectId, { enabled_sections: enabledSections, model_tier: modelTier });
      setSessionStatus('processing');
      setPhases(null);
      setOverallPercentage(0);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to start generation'));
    }
  }, [projectId, enabledSections, modelTier]);

  const handleRegenerate = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setTotalDurationSeconds(null);
      await regenerateCAB(projectId, { enabled_sections: enabledSections, model_tier: modelTier });
      setSessionStatus('processing');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to regenerate'));
    }
  }, [projectId, enabledSections, modelTier]);

  const handlePause = useCallback(async () => {
    if (!projectId) return;
    try {
      await pauseCAB(projectId);
      setSessionStatus('paused');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to pause'));
    }
  }, [projectId]);

  const handleStop = useCallback(async () => {
    if (!projectId) return;
    try {
      await cancelCAB(projectId);
      setSessionStatus('cancelled');
      setPhases(null);
      setPreviewHtml(null);
      setOverallPercentage(0);
      setCurrentProgressStage(null);
      setTotalDurationSeconds(null);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to stop'));
    }
  }, [projectId]);

  const handleResume = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      await resumeCAB(projectId);
      setSessionStatus('processing');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to resume'));
    }
  }, [projectId]);

  // ────────────────────────────────────────────────────────────
  // COMPUTED
  // ────────────────────────────────────────────────────────────

  const hasFiles = files.length > 0 || uploadingFiles.length > 0;
  const isProcessing = sessionStatus === 'processing';
  const hasGenerationStarted = sessionStatus === 'processing' || sessionStatus === 'paused' || sessionStatus === 'completed' || sessionStatus === 'error';
  const canGenerate = files.length > 0 && !isProcessing && !isUploading && enabledSections.length > 0;
  const showProgressSidebar = isProcessing || sessionStatus === 'paused' || sessionStatus === 'error';

  // Determine preview empty state variant
  // Keep showing 'no-files' until generation starts to avoid message flicker
  const previewVariant: 'no-files' | 'ready-to-generate' | 'processing' = isProcessing
    ? 'processing'
    : 'no-files';

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-bg-v2-xl overflow-hidden w-full">
      {/* ═══════ LEFT: Files + Controls + Progress Sidebar ═══════ */}
      <div className="flex-1 min-w-0 flex gap-bg-v2-xl">
        {/* Main content panel */}
        <div className="flex-1 min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
          {/* Header — min-h matches the preview panel header (icon buttons add height) */}
          <div className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary flex items-center min-h-[56px] shrink-0">
            <Text variant="text-md" weight="semibold" className="text-text-primary">
              Upload Files
            </Text>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-bg-v2-xl">
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <CABFileUpload
                variant={hasFiles ? 'compact' : 'large'}
                onFilesSelected={handleFilesSelected}
                disabled={hasGenerationStarted}
                isUploading={isUploading}
              />
            </IfHasPermission>
            <CABFileList
              files={files}
              uploadingFiles={uploadingFiles}
              onDeleteFile={handleDeleteFile}
              onDownloadFile={handleDownloadFile}
              disabled={hasGenerationStarted}
              permissions={permissions}
            />
            {hasFiles && (
              <>
                <CABSectionSelector
                  enabledSections={enabledSections}
                  onChange={setEnabledSections}
                  disabled={hasGenerationStarted}
                  readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                />
                <CABModelSelector
                  selectedTier={modelTier}
                  onChange={setModelTier}
                  disabled={hasGenerationStarted}
                  readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                />
              </>
            )}
            {error && (
              <div className="mt-bg-v2-md p-bg-v2-md bg-bg-error-primary rounded-bg-v2-md">
                <Text variant="text-xs" className="text-text-error-primary">{error}</Text>
              </div>
            )}
          </div>

          {/* Cost panel */}
          {costInfo && (
            <CABCostPanel
              cost={costInfo}
              visible={sessionStatus === 'completed' || sessionStatus === 'error' || (costInfo.total_usd > 0)}
              totalDurationSeconds={totalDurationSeconds}
            />
          )}

          {/* Action bar */}
          <CABActionBar
            status={sessionStatus}
            canGenerate={canGenerate}
            hasFiles={hasFiles}
            onGenerate={handleGenerate}
            onPause={handlePause}
            onStop={handleStop}
            onDeleteAll={handleDeleteAll}
            onResume={handleResume}
            isUploading={isUploading}
            permissions={permissions}
            platformType={platformType}
            onGenerateDesign={async () => {
              if (!projectId || !onGenerateDesign) return;
              onGenerateDesign();
            }}
          />
        </div>

        {/* Progress sidebar */}
        <CABProgressSidebar
          phases={phases}
          visible={showProgressSidebar}
          sessionStatus={sessionStatus}
        />
      </div>

      {/* ═══════ RIGHT: Preview Panel ═══════ */}
      <div className="flex-1 min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
        {/* Preview header */}
        <div className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary flex justify-between items-center min-h-[56px] shrink-0">
          <div>
            <Text variant="text-md" weight="semibold" className="text-text-primary">
              Common Application Blueprint Preview
            </Text>
          </div>
          <div className="flex gap-bg-v2-sm">
            <Button
              type="button"
              label={<Icon name={IconName.ExternalLink} size="sm" inheritColor />}
              onClick={() => {
                if (!projectId) return;
                window.open(`/project/${projectId}/cab`, '_blank');
              }}
              variant="outline"
              size="very-small"
              className="!w-8 !h-8 !p-0 !min-w-0"
              disabled={sessionStatus !== 'completed'}
              title="Open in New Tab"
              tooltipPosition="bottom"
            />
            <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
              <Button
                type="button"
                label={<Icon name={IconName.Share} size="sm" inheritColor />}
                onClick={() => setShowShareModal(true)}
                variant="outline"
                size="very-small"
                className="!w-8 !h-8 !p-0 !min-w-0"
                disabled={sessionStatus !== 'completed'}
                title="Share CAB"
                tooltipPosition="bottom"
              />
            </IfHasPermission>
            <Button
              type="button"
              label={isDownloadingMarkdown ? null : <Icon name={IconName.FileDownload} size="sm" inheritColor />}
              onClick={async () => {
                if (!projectId || isDownloadingMarkdown) return;
                setIsDownloadingMarkdown(true);
                try {
                  await downloadCABMarkdown(projectId);
                } catch (err) {
                  console.error('Failed to download CAB Markdown:', err);
                } finally {
                  setIsDownloadingMarkdown(false);
                }
              }}
              variant="outline"
              size="very-small"
              className="!w-8 !h-8 !p-0 !min-w-0"
              disabled={sessionStatus !== 'completed'}
              loading={isDownloadingMarkdown}
              title={isDownloadingMarkdown ? 'Downloading…' : 'Download Markdown'}
              tooltipPosition="bottom"
            />
            <Button
              type="button"
              label={isDownloadingPdf ? null : <Icon name={IconName.PdfDownload} size="sm" inheritColor />}
              onClick={async () => {
                if (!projectId || isDownloadingPdf) return;
                setIsDownloadingPdf(true);
                try {
                  await downloadCABPdf(projectId);
                } catch (err) {
                  console.error('Failed to download CAB PDF:', err);
                } finally {
                  setIsDownloadingPdf(false);
                }
              }}
              variant="outline"
              size="very-small"
              className="!w-8 !h-8 !p-0 !min-w-0"
              disabled={sessionStatus !== 'completed'}
              loading={isDownloadingPdf}
              title={isDownloadingPdf ? 'Generating PDF…' : 'Download PDF'}
              tooltipPosition="bottom"
            />
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 overflow-hidden">
          {previewHtml ? (
            <iframe
              srcDoc={previewHtml}
              className="w-full h-full border-0"
              title="CAB Preview"
              sandbox="allow-scripts allow-same-origin"
            />
          ) : isLoadingPreview ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
            </div>
          ) : sessionStatus === 'error' && errorInfo ? (
            <CABErrorState
              error={errorInfo}
              onResume={handleResume}
              onRegenerate={handleRegenerate}
            />
          ) : (
            <CABEmptyState
              variant={previewVariant}
              fileCount={files.length}
            />
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <CABShareModal
          projectId={projectId!}
          shareableLinkEnabled={shareableLinkEnabled}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSave={async (enabled: boolean) => {
            const result = await updateCABShareSettings(projectId!, enabled);
            setShareableLinkEnabled(result.readonly_sharable_link_enabled);
          }}
        />
      )}
    </div>
  );
};

export default CABPage;
